/**
 * Search result quality sorting utility
 * 
 * Prioritizes high-quality results (with titles, covers, no junk keywords)
 * and deprioritizes low-quality results (missing titles, "Paperback", "Illustrated Edition", etc.)
 */

// Keywords that indicate lower-quality or variant editions
const LOW_PRIORITY_KEYWORDS = [
  'paperback',
  'hardcover', 
  'illustrated edition',
  'deluxe edition',
  'collector\'s edition',
  'mass market',
  'trade paperback',
  'large print',
  'audio cd',
  'audiobook',
  'box set',
  'boxed set',
  'anniversary edition',
  'special edition',
  'movie tie-in',
  'film tie-in',
];

interface SortableBook {
  id?: string;
  title: string;
  authors?: string[];
  cover_url?: string;
  coverUrl?: string;
  isbn13?: string;
  open_library_id?: string;
  isLocal?: boolean;
}

/**
 * Calculate a quality score for sorting search results
 * Higher scores = better quality = shown first
 */
export function calculateSearchQualityScore(book: SortableBook): number {
  let score = 50; // Base score

  // Major penalty: Missing or empty title
  if (!book.title || book.title.trim() === '' || book.title === 'Untitled') {
    score -= 100; // Will be sorted to the end
  }

  // Bonus: Has cover image
  if (book.cover_url || book.coverUrl) {
    score += 15;
  }

  // Bonus: Has authors
  if (book.authors && book.authors.length > 0 && book.authors[0]) {
    score += 10;
  }

  // Bonus: Has ISBN (more reliable metadata)
  if (book.isbn13) {
    score += 5;
  }

  // Bonus: Is a local book (already in our database)
  if (book.isLocal) {
    score += 10;
  }

  // Penalty: Title contains low-priority keywords
  const lowerTitle = book.title?.toLowerCase() || '';
  for (const keyword of LOW_PRIORITY_KEYWORDS) {
    if (lowerTitle.includes(keyword)) {
      score -= 20;
      break; // Only apply penalty once
    }
  }

  // Additional penalty for titles that are ONLY format descriptors
  // e.g., "[Paperback]" or "Illustrated Edition"
  const titleWithoutBrackets = lowerTitle.replace(/[\[\]()]/g, '').trim();
  if (LOW_PRIORITY_KEYWORDS.some(kw => titleWithoutBrackets === kw)) {
    score -= 30;
  }

  return score;
}

/**
 * Sort search results by quality score (descending)
 * High-quality results appear first
 */
export function sortBySearchQuality<T extends SortableBook>(results: T[]): T[] {
  return [...results].sort((a, b) => {
    const scoreA = calculateSearchQualityScore(a);
    const scoreB = calculateSearchQualityScore(b);
    return scoreB - scoreA; // Descending order
  });
}
